import { Routes } from '@angular/router';
import { authGuard, signedInGuard } from './core/auth/auth.guard';
import { businessAccountGuard } from './core/auth/business-account.guard';

/**
 * Six product sections: Dashboard, Treasury, Business, Reports, Compliance, Settings.
 *
 * Every pre-restructure URL is kept alive as a redirect at the bottom of this file —
 * bookmarks, emailed links and the KlidiPay payment links already in the wild all still
 * resolve. Don't delete them without checking what points at them.
 */
export const routes: Routes = [
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },

  // ── Hosted checkout (anonymous, token-keyed — no auth guard, no merchant context) ───────
  {
    path: 'pay/:token',
    loadComponent: () => import('./features/checkout/checkout-page/checkout-page.component').then(m => m.CheckoutPageComponent)
  },
  // A shop's permanent payment link: any amount, into the shop's own vault.
  {
    path: 'shop/:token',
    loadComponent: () => import('./features/checkout/shop-payment-page/shop-payment-page.component').then(m => m.ShopPaymentPageComponent)
  },

  // ── Auth ────────────────────────────────────────────────────────────────
  // No sign-in screen of our own: Entra External ID owns the credentials, so an unauthenticated
  // visitor is sent straight to its user flow by the guards. /login-failed is where MSAL lands a
  // cancelled or failed attempt, and is deliberately unguarded so it cannot bounce back into another
  // redirect.
  {
    path: 'login-failed',
    loadComponent: () => import('./features/auth/login-failed/login-failed.component').then(m => m.LoginFailedComponent)
  },

  // ── Registration (BC_Onboarding_UI's flow) ──────────────────────────────
  // /sign-up starts the registration handshake and opens the External ID sign-up page; back from it,
  // AuthService sends anyone not yet registered to the /register wizard.
  {
    path: 'sign-up',
    loadComponent: () => import('./features/registration/sign-up/sign-up.component').then(m => m.SignUpComponent)
  },
  {
    path: 'register',
    canActivate: [signedInGuard],
    loadComponent: () => import('./features/registration/onboarding/onboarding.component').then(m => m.OnboardingComponent)
  },
  {
    // Someone who asked to manage a client that already exists waits here for approval.
    path: 'access-request',
    canActivate: [signedInGuard],
    loadComponent: () => import('./features/registration/customers-onboarding/components/access-request/access-request.component')
      .then(m => m.AccessRequestComponent)
  },

  // ── Application (BC_Onboarding_UI's /auth/* pages, inside Klidi's shell) ──
  // The client application the wizard leads into, its status pages, account settings, the
  // introducer pages and the migration applications. All of it works on the client's Corporate
  // form, not the Klidi merchant, so a signed-in account is enough (as for /register).
  {
    path: 'application',
    canActivate: [signedInGuard],
    loadComponent: () => import('./features/registration/application-shell.component').then(m => m.ApplicationShellComponent),
    loadChildren: () => import('./features/registration/application.routes').then(m => m.applicationRoutes)
  },

  // The earlier one-screen profile step, kept alongside the wizard.
  {
    path: 'onboarding',
    canActivate: [signedInGuard],
    loadComponent: () => import('./features/auth/onboarding/onboarding.component').then(m => m.OnboardingComponent)
  },

  // ── Dashboard ───────────────────────────────────────────────────────────
  {
    path: 'dashboard',
    canActivate: [authGuard],
    loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent)
  },

  // ── Treasury ────────────────────────────────────────────────────────────
  { path: 'treasury', redirectTo: 'treasury/holdings', pathMatch: 'full' },
  {
    path: 'treasury/holdings',
    canActivate: [authGuard],
    loadComponent: () => import('./features/treasury/holdings/holdings.component').then(m => m.HoldingsComponent)
  },
  {
    path: 'treasury/activity',
    canActivate: [authGuard],
    loadComponent: () => import('./features/treasury/activity/activity.component').then(m => m.ActivityComponent)
  },

  // Treasury detail pages — reached from Holdings/Activity, not from the sidebar.
  {
    path: 'treasury/deposit',
    canActivate: [authGuard],
    loadComponent: () => import('./features/deposit/deposit/deposit.component').then(m => m.DepositComponent)
  },
  {
    path: 'treasury/withdraw',
    canActivate: [authGuard],
    loadComponent: () => import('./features/withdraw/withdraw/withdraw.component').then(m => m.WithdrawComponent)
  },
  {
    path: 'treasury/trade',
    canActivate: [authGuard],
    loadComponent: () => import('./features/trading/buy-sell/buy-sell.component').then(m => m.BuySellComponent)
  },
  {
    path: 'treasury/bank-accounts',
    canActivate: [authGuard],
    loadComponent: () => import('./features/banking/banking/banking.component').then(m => m.BankingComponent)
  },
  {
    path: 'treasury/bank-accounts/add',
    canActivate: [authGuard],
    loadComponent: () => import('./features/banking/add-bank-account/add-bank-account.component').then(m => m.AddBankAccountComponent)
  },
  {
    path: 'treasury/crypto-wallets',
    canActivate: [authGuard],
    loadComponent: () => import('./features/crypto-wallets/crypto-wallets/crypto-wallets.component').then(m => m.CryptoWalletsComponent)
  },
  {
    path: 'treasury/crypto-wallets/add',
    canActivate: [authGuard],
    loadComponent: () => import('./features/crypto-wallets/add-wallet/add-wallet.component').then(m => m.AddWalletComponent)
  },
  {
    path: 'treasury/cards',
    canActivate: [authGuard],
    loadComponent: () => import('./features/cards/cards/cards.component').then(m => m.CardsComponent)
  },
  {
    path: 'treasury/cards/order',
    canActivate: [authGuard],
    loadComponent: () => import('./features/cards/order-card/order-card.component').then(m => m.OrderCardComponent)
  },
  {
    path: 'treasury/payment-methods',
    canActivate: [authGuard],
    loadComponent: () => import('./features/payment-methods/payment-methods/payment-methods.component').then(m => m.PaymentMethodsComponent)
  },
  {
    path: 'treasury/payment-methods/add',
    canActivate: [authGuard],
    loadComponent: () => import('./features/payment-methods/add-payment-method/add-payment-method.component').then(m => m.AddPaymentMethodComponent)
  },

  // ── Business ────────────────────────────────────────────────────────────
  { path: 'business', redirectTo: 'business/overview', pathMatch: 'full' },
  {
    path: 'business/overview',
    canActivate: [authGuard],
    loadComponent: () => import('./features/business/overview/overview.component').then(m => m.OverviewComponent)
  },
  {
    path: 'business/entities/new',
    canActivate: [authGuard],
    loadComponent: () => import('./features/business-accounts/add-business-account/add-business-account.component')
      .then(m => m.AddBusinessAccountComponent)
  },
  {
    path: 'business/team/add',
    canActivate: [authGuard],
    loadComponent: () => import('./features/users-permissions/add-user/add-user.component').then(m => m.AddUserComponent)
  },
  {
    path: 'business/klidipay',
    canActivate: [authGuard, businessAccountGuard],
    loadComponent: () => import('./features/business/klidipay/klidipay.component').then(m => m.KlidiPayComponent)
  },
  {
    path: 'business/klidipay/add-shop',
    canActivate: [authGuard, businessAccountGuard],
    loadComponent: () => import('./features/shops/add-shop/add-shop.component').then(m => m.AddShopComponent)
  },
  {
    path: 'business/klidipay/:shopId/edit',
    canActivate: [authGuard, businessAccountGuard],
    loadComponent: () => import('./features/shops/add-shop/add-shop.component').then(m => m.AddShopComponent)
  },
  {
    path: 'business/klidipay/:shopId/orders',
    canActivate: [authGuard, businessAccountGuard],
    loadComponent: () => import('./features/shops/shop-orders/shop-orders.component').then(m => m.ShopOrdersComponent)
  },
  {
    path: 'business/klidipay/api-orders',
    canActivate: [authGuard, businessAccountGuard],
    loadComponent: () => import('./features/api-pos-orders/api-pos-orders/api-pos-orders.component').then(m => m.ApiPosOrdersComponent)
  },

  // ── Reports ─────────────────────────────────────────────────────────────
  { path: 'reports', redirectTo: 'reports/intelligence', pathMatch: 'full' },
  {
    path: 'reports/intelligence',
    canActivate: [authGuard],
    loadComponent: () => import('./features/reports/intelligence/intelligence.component').then(m => m.IntelligenceComponent)
  },
  {
    path: 'reports/estatements',
    canActivate: [authGuard],
    loadComponent: () => import('./features/reports/estatements/estatements.component').then(m => m.EstatementsComponent)
  },
  {
    path: 'reports/performance',
    canActivate: [authGuard],
    loadComponent: () => import('./features/reports/performance/performance.component').then(m => m.PerformanceComponent)
  },
  {
    path: 'reports/fees',
    canActivate: [authGuard],
    loadComponent: () => import('./features/fees/fees/fees.component').then(m => m.FeesComponent)
  },
  {
    path: 'reports/affiliate',
    canActivate: [authGuard],
    loadComponent: () => import('./features/affiliate/affiliate/affiliate.component').then(m => m.AffiliateComponent)
  },

  // ── Compliance ──────────────────────────────────────────────────────────
  { path: 'compliance', redirectTo: 'compliance/verification', pathMatch: 'full' },
  {
    path: 'compliance/verification',
    canActivate: [authGuard],
    loadComponent: () => import('./features/kyc/kyc/kyc.component').then(m => m.KycComponent)
  },
  {
    path: 'compliance/security',
    canActivate: [authGuard],
    loadComponent: () => import('./features/compliance/security/security-centre.component').then(m => m.SecurityCentreComponent)
  },
  {
    path: 'compliance/audit-log',
    canActivate: [authGuard],
    loadComponent: () => import('./features/compliance/audit-log/audit-log.component').then(m => m.AuditLogComponent)
  },
  {
    path: 'compliance/documents',
    canActivate: [authGuard],
    loadComponent: () => import('./features/documents/documents.component').then(m => m.DocumentsComponent)
  },

  // ── Settings ────────────────────────────────────────────────────────────
  { path: 'settings', redirectTo: 'settings/profile', pathMatch: 'full' },
  {
    path: 'settings/profile',
    canActivate: [authGuard],
    loadComponent: () => import('./features/profile/profile.component').then(m => m.ProfileComponent)
  },
  {
    path: 'settings/api-keys',
    canActivate: [authGuard],
    loadComponent: () => import('./features/settings/api-keys/api-keys.component').then(m => m.ApiKeysComponent)
  },
  {
    path: 'settings/notifications',
    canActivate: [authGuard],
    loadComponent: () => import('./features/settings/notifications/notifications.component').then(m => m.NotificationsComponent)
  },
  {
    // Same TwoFactorComponent the Compliance > Security page embeds. The spec lists 2FA
    // under both sections; one component, two entry points, no duplicated state.
    path: 'settings/two-factor',
    canActivate: [authGuard],
    loadComponent: () => import('./features/settings/two-factor-page/two-factor-page.component').then(m => m.TwoFactorPageComponent)
  },
  {
    path: 'settings/api-docs',
    canActivate: [authGuard],
    loadComponent: () => import('./features/api-docs/api-docs/api-docs.component').then(m => m.ApiDocsComponent)
  },

  // ── Outside the six sections ────────────────────────────────────────────
  {
    path: 'help-support',
    canActivate: [authGuard],
    loadComponent: () => import('./features/help-support/help-support.component').then(m => m.HelpSupportComponent)
  },
  {
    path: 'administration',
    canActivate: [authGuard, businessAccountGuard],
    loadComponent: () => import('./features/administration/administration.component').then(m => m.AdministrationComponent)
  },
  // KYC review, Scrypt diagnostics and crypto gates are BizCombo staff tools. They live in the CMS, which staff
  // sign in to with bizcombo.tech; the API refuses them to every merchant token.

  // ── Public (no guard) ───────────────────────────────────────────────────
  {
    // "Do you have a reference number?" gate shown after registration. Public —
    // the user may still be unauthenticated at this point in the sign-up flow.
    path: 'reference-check',
    loadComponent: () => import('./features/forms-lookup/reference-prompt/reference-prompt.component').then(m => m.ReferencePromptComponent)
  },
  {
    path: 'form-lookup',
    loadComponent: () => import('./features/forms-lookup/forms-lookup.component').then(m => m.FormsLookupComponent)
  },
  {
    // Confirmation page after a successful compliance submission. Public — the
    // user may still be unauthenticated at this point in the sign-up flow.
    path: 'form-lookup/submitted',
    loadComponent: () => import('./features/forms-lookup/thank-you/thank-you.component').then(m => m.ThankYouComponent)
  },

  // ── Legacy URLs ─────────────────────────────────────────────────────────
  // Pre-restructure paths. Kept so existing bookmarks and any payment/shop links
  // already sent to customers keep resolving. Order matters: the more specific
  // paths must come before their parents.
  { path: 'assets',                   redirectTo: 'treasury/holdings',        pathMatch: 'full' },
  { path: 'trade',                    redirectTo: 'treasury/trade',           pathMatch: 'full' },
  { path: 'transactions',             redirectTo: 'treasury/activity',        pathMatch: 'full' },
  { path: 'deposit',                  redirectTo: 'treasury/deposit',         pathMatch: 'full' },
  { path: 'withdraw',                 redirectTo: 'treasury/withdraw',        pathMatch: 'full' },
  { path: 'banking/add',              redirectTo: 'treasury/bank-accounts/add',   pathMatch: 'full' },
  { path: 'banking',                  redirectTo: 'treasury/bank-accounts',       pathMatch: 'full' },
  { path: 'crypto-wallets/add',       redirectTo: 'treasury/crypto-wallets/add',  pathMatch: 'full' },
  { path: 'crypto-wallets',           redirectTo: 'treasury/crypto-wallets',      pathMatch: 'full' },
  { path: 'cards/order',              redirectTo: 'treasury/cards/order',     pathMatch: 'full' },
  { path: 'cards',                    redirectTo: 'treasury/cards',           pathMatch: 'full' },
  { path: 'payment-methods/add',      redirectTo: 'treasury/payment-methods/add', pathMatch: 'full' },
  { path: 'payment-methods',          redirectTo: 'treasury/payment-methods',     pathMatch: 'full' },

  { path: 'business-accounts/new',    redirectTo: 'business/entities/new',    pathMatch: 'full' },
  { path: 'business-accounts',        redirectTo: 'business/overview',        pathMatch: 'full' },
  { path: 'users-permissions/add',    redirectTo: 'business/team/add',        pathMatch: 'full' },
  { path: 'users-permissions',        redirectTo: 'business/overview',        pathMatch: 'full' },
  { path: 'shops/add',                redirectTo: 'business/klidipay/add-shop',   pathMatch: 'full' },
  { path: 'shops/:shopId/orders',     redirectTo: 'business/klidipay/:shopId/orders' },
  { path: 'shops',                    redirectTo: 'business/klidipay',        pathMatch: 'full' },
  { path: 'klidi-pay',                redirectTo: 'business/klidipay',        pathMatch: 'full' },
  { path: 'api-pos-orders',           redirectTo: 'business/klidipay/api-orders', pathMatch: 'full' },

  { path: 'balance-report',           redirectTo: 'reports/estatements',      pathMatch: 'full' },
  { path: 'fees',                     redirectTo: 'reports/fees',             pathMatch: 'full' },
  { path: 'affiliate',                redirectTo: 'reports/affiliate',        pathMatch: 'full' },

  { path: 'kyc',                      redirectTo: 'compliance/verification',  pathMatch: 'full' },
  { path: 'security',                 redirectTo: 'compliance/security',      pathMatch: 'full' },
  { path: 'whitelist',                redirectTo: 'compliance/security',      pathMatch: 'full' },
  { path: 'documents',                redirectTo: 'compliance/documents',     pathMatch: 'full' },

  { path: 'profile',                  redirectTo: 'settings/profile',         pathMatch: 'full' },
  { path: 'api-docs',                 redirectTo: 'settings/api-docs',        pathMatch: 'full' },
  { path: 'support',                  redirectTo: 'help-support',             pathMatch: 'full' },

  { path: '**', redirectTo: 'dashboard' }
];
